import type { MailAddressValue, MailboxStatusView, RecipientCheck } from '@fos/shared/contracts/mail';
import { formatRelativePast } from '../../lib/format';

/** „Anna Kowalczyk <anna@rea.pl>” albo sam adres. */
export function addressLine(a: MailAddressValue): string {
  return a.name ? `${a.name} <${a.email}>` : a.email;
}

/** Krótko do nagłówka wiadomości: imię i nazwisko albo adres. */
export function addressShort(a: MailAddressValue): string {
  return a.name?.trim() || a.email;
}

/** Lista adresów w polu formularza: „Anna Kowalczyk <anna@rea.pl>, biuro@rea.pl”. */
export function addressesText(list: readonly MailAddressValue[]): string {
  return list.map(addressLine).join(', ');
}

const EMAIL = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;

/**
 * Pole „Do” / „DW”: adresy rozdzielone przecinkiem, średnikiem albo nową linią; „Imię <adres>” albo sam adres.
 * Zwraca poprawne adresy (małe litery, bez powtórzeń) i fragmenty, których nie da się odczytać.
 */
export function parseAddressInput(text: string): { list: MailAddressValue[]; invalid: string[] } {
  const list: MailAddressValue[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const raw of text.split(/[,;\n]+/)) {
    const part = raw.trim();
    if (!part) continue;
    const m = /^(.*)<([^<>]+)>$/.exec(part);
    const email = (m ? (m[2] ?? '') : part).trim().toLowerCase();
    const name = m ? (m[1] ?? '').trim().replace(/^"|"$/g, '') : '';
    if (!EMAIL.test(email)) {
      invalid.push(part);
      continue;
    }
    if (seen.has(email)) continue;
    seen.add(email);
    list.push(name ? { email, name } : { email });
  }
  return { list, invalid };
}

/** „Ostatnia synchronizacja: 2 min temu” (albo „jeszcze nie było”). */
export function lastSyncText(mailbox: Pick<MailboxStatusView, 'lastSyncAt'>, now: Date = new Date()): string {
  return mailbox.lastSyncAt
    ? `Ostatnia synchronizacja: ${formatRelativePast(new Date(mailbox.lastSyncAt), now)}`
    : 'Jeszcze nie synchronizowano';
}

/** Opis odbiorcy wymagającego potwierdzenia (Etap 5: niejednoznaczny odbiorca blokuje wysyłkę). */
export function recipientWarning(check: RecipientCheck): string {
  const names = check.clients.map((c) => c.name).filter((n): n is string => Boolean(n));
  switch (check.status) {
    case 'ambiguous':
      return names.length
        ? `${check.email} jest kontaktem kilku klientów (${names.join(', ')}).`
        : `${check.email} jest kontaktem kilku klientów.`;
    case 'other_client':
      return names.length
        ? `${check.email} jest kontaktem innego klienta (${names.join(', ')}).`
        : `${check.email} jest kontaktem innego klienta.`;
    case 'new_address':
      return `${check.email} nie jest kontaktem tego klienta.`;
    default:
      return check.email;
  }
}
